/**
 * 快手医典
 * 接口文档：https://kapi.corp.kuaishou.com/project/10525/interface/api/cat_104406
 * @author xianghai
 */
import {
  IDepartmentInfo,
  IDepartmentInfoV2,
  IDiseaseCategory,
  IDiseaseDepartment,
  IDiseaseInfo,
  ISearchDiseaseResponse,
  ISearchDiseaseResponseV2
} from '@/schemas/med-dictionary';
import { checkResponse } from '@/utils/helpers';
import { request } from './request';
import { ApiConfigs, Response, ResponseV2 } from '../schemas/service';

const api: ApiConfigs<
  | 'commonCategorys'
  | 'diseaseDepartments'
  | 'diseaseDepartmentsV2'
  | 'diseaseDepartmentsInfo'
  | 'diseaseSubscribe'
  | 'cancelDiseaseSubscribe'
  | 'searchCarouselWord'
  | 'searchHotWords'
  | 'searchDisease'
  | 'diseaseDepartmentsInfoV2'
  | 'diseaseInfoV2'
  | 'searchDiseaseV2'
  | 'diseaseSubscribeV2'
  | 'cancelDiseaseSubscribeV2'
> = {
  // 常见问题疾病目录
  commonCategorys: {
    url: '/health/api/v1/medical-dict/disease/categories',
    method: 'GET'
  },
  // 所有疾病科室
  diseaseDepartments: {
    url: '/health/api/v1/medical-dict/departments',
    method: 'GET'
  },
  diseaseDepartmentsV2: {
    url: '/health/api/v2/medical-dict/departments',
    method: 'GET'
  },
  diseaseDepartmentsInfo: {
    url: '/health/api/v2/medical-dict/department-info',
    method: 'GET'
  },
  diseaseSubscribe: {
    url: '/health/api/v1/medical-dict/disease/subscribe',
    method: 'POST'
  },
  cancelDiseaseSubscribe: {
    url: '/health/api/v1/medical-dict/disease/unsubscribe',
    method: 'POST'
  },
  searchCarouselWord: {
    url: '/health/api/v1/medical-dict/search/words',
    method: 'GET'
  },
  searchHotWords: {
    url: '/health/api/v1/medical-dict/search/hot-words',
    method: 'GET'
  },
  searchDisease: {
    url: '/health/api/v1/medical-dict/search',
    method: 'GET'
  },
  diseaseDepartmentsInfoV2: {
    url: '/health/api/v2/medical-dict/department-info',
    method: 'GET'
  },
  diseaseInfoV2: {
    url: '/health/api/v2/medical-dict/disease-info',
    method: 'GET'
  },
  searchDiseaseV2: {
    url: '/health/api/v2/medical-dict/search',
    method: 'GET'
  },
  diseaseSubscribeV2: {
    url: '/health/api/v2/medical-dict/disease/subscribe',
    method: 'POST'
  },
  cancelDiseaseSubscribeV2: {
    url: '/health/api/v2/medical-dict/disease/unsubscribe',
    method: 'POST'
  }
};

const medDictionary = {
  async getCommonCategorys() {
    try {
      const response: Response<IDiseaseCategory[]> = await request(
        api.commonCategorys
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDiseaseDepartments() {
    try {
      const response: Response<IDiseaseDepartment[]> = await request(
        api.diseaseDepartments
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDiseaseDepartmentsV2() {
    try {
      const response: Response<IDiseaseDepartment[]> = await request(
        api.diseaseDepartmentsV2
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDiseaseDepartmentsInfo(params: { departmentId: number }) {
    try {
      const response: Response<IDepartmentInfo> = await request(
        api.diseaseDepartmentsInfo,
        params
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async diseaseSubscribe(data: { diseaseItemId: string }) {
    try {
      const response: Response<string[]> = await request(
        api.diseaseSubscribe,
        undefined,
        data
      );
      const { code, result } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async cancelDiseaseSubscribe(data: { diseaseItemId: string }) {
    try {
      const response: Response<string[]> = await request(
        api.cancelDiseaseSubscribe,
        undefined,
        data
      );
      const { code, result } = response;
      return checkResponse(code, result);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getSearchCarouselWord() {
    try {
      const response: Response<string[]> = await request(
        api.searchCarouselWord
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async searchHotWords() {
    try {
      const response: Response<string[]> = await request(api.searchHotWords);
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async searchDisease(params: { searchWord: string }) {
    try {
      const response: Response<ISearchDiseaseResponse> = await request(
        api.searchDisease,
        params
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDiseaseInfo(params: { diseaseItemId: string }) {
    try {
      const response: ResponseV2<IDiseaseInfo> = await request(
        api.diseaseInfoV2,
        params
      );
      const { data } = response;
      const { diseaseAspects } = data;
      if (diseaseAspects) {
        for (let i = diseaseAspects.length - 1; i >= 0; i--) {
          for (let j = i - 1; j >= 0; j--) {
            const videoVos2 = diseaseAspects[j].videoVos;
            diseaseAspects[i].videoVos = diseaseAspects[i].videoVos.filter(
              (video) =>
                video.photoId !==
                videoVos2.find((video2) => video.photoId === video2.photoId)
                  ?.photoId
            );
          }
        }
      }
      return {
        ...data,
        videoCount: data.diseaseAspects.reduce(
          (pre, curAspects) => pre + curAspects.videoVos.length,
          0
        )
      };
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getDiseaseDepartmentsInfoV2(params: { departmentId: number }) {
    try {
      const response: ResponseV2<IDepartmentInfoV2> = await request(
        api.diseaseDepartmentsInfoV2,
        params
      );
      const { data } = response;
      return data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async searchDiseaseV2(params: { searchWord: string }) {
    try {
      const response: ResponseV2<ISearchDiseaseResponseV2> = await request(
        api.searchDiseaseV2,
        params
      );
      const { data } = response;
      return data;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async cancelSubscribeDiseaseV2(params: { departmentId: number }) {
    try {
      const response: ResponseV2<number> = await request(
        api.searchDiseaseV2,
        params
      );
      const { data } = response;
      return data;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default medDictionary;
